/**
 * PGN (Portable Game Notation) import/export functionality
 * Handles reading and writing chess games in standard PGN format
 */

import { GameState, PieceType, Move } from './types';
import { parseFEN, toFEN, algebraicToSquare } from './board';
import { generateLegalMoves, makeMove } from './moves';
import { AnnotatedMove } from './notation';

/**
 * PGN metadata tags
 */
export interface PGNTags {
  Event?: string;
  Site?: string;
  Date?: string;
  Round?: string;
  White?: string;
  Black?: string;
  Result?: string;
  [key: string]: string | undefined;
}

/**
 * Represents a complete PGN game
 */
export interface PGNGame {
  tags: PGNTags;
  moves: AnnotatedMove[];
  initialFEN?: string; // If game doesn't start from standard position
}

/**
 * Export game to PGN format
 */
export function exportToPGN(
  moves: AnnotatedMove[],
  tags?: Partial<PGNTags>,
  initialFEN?: string
): string {
  let pgn = '';
  
  // Default tags
  const defaultTags: PGNTags = {
    Event: tags?.Event || 'Casual Game',
    Site: tags?.Site || 'Local Browser',
    Date: tags?.Date || new Date().toISOString().split('T')[0].replace(/-/g, '.'),
    Round: tags?.Round || '?',
    White: tags?.White || 'Player 1',
    Black: tags?.Black || 'Player 2',
    Result: tags?.Result || '*',
  };
  
  // Add FEN tag if not starting from standard position
  if (initialFEN && initialFEN !== 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1') {
    defaultTags.FEN = initialFEN;
    defaultTags.SetUp = '1';
  }
  
  // Write tags
  const tagOrder = ['Event', 'Site', 'Date', 'Round', 'White', 'Black', 'Result'];
  for (const key of tagOrder) {
    if (defaultTags[key]) {
      pgn += `[${key} "${defaultTags[key]}"]\n`;
    }
  }
  
  // Write any additional tags
  for (const [key, value] of Object.entries(defaultTags)) {
    if (!tagOrder.includes(key) && value) {
      pgn += `[${key} "${value}"]\n`;
    }
  }
  
  pgn += '\n';
  
  // Write moves
  if (moves.length === 0) {
    pgn += defaultTags.Result || '*';
  } else {
    let moveText = '';
    for (let i = 0; i < moves.length; i++) {
      const move = moves[i];
      
      // Add move number for white moves
      if (i % 2 === 0) {
        const moveNumber = Math.floor(i / 2) + 1;
        if (moveText) moveText += ' ';
        moveText += `${moveNumber}.`;
      }
      
      // Add move notation
      moveText += ` ${move.notation}`;
      
      // Add line breaks every ~80 characters for readability
      if (moveText.length > 80 && i < moves.length - 1) {
        pgn += moveText + '\n';
        moveText = '';
      }
    }
    
    if (moveText) {
      pgn += moveText;
    }
    
    // Add result at end
    pgn += ' ' + (defaultTags.Result || '*');
  }
  
  return pgn;
}

/**
 * Parse PGN tags from text
 */
function parseTags(pgnText: string): PGNTags {
  const tags: PGNTags = {};
  const tagRegex = /\[(\w+)\s+"([^"]*)"\]/g;
  let match;
  
  while ((match = tagRegex.exec(pgnText)) !== null) {
    const [, key, value] = match;
    tags[key] = value;
  }
  
  return tags;
}

/**
 * Parse a single move in algebraic notation
 * Returns the matching legal move, or null if no match found
 */
function parseAlgebraicMove(
  notation: string,
  gameState: GameState
): Move | null {
  // Remove check/checkmate indicators
  const cleanNotation = notation.replace(/[+#]$/, '');
  
  // Handle castling
  if (cleanNotation === 'O-O' || cleanNotation === 'O-O-O') {
    const moves = generateLegalMoves(gameState);
    return moves.find(m => 
      m.isCastling && 
      (cleanNotation === 'O-O' ? m.to > m.from : m.to < m.from)
    ) || null;
  }
  
  const moves = generateLegalMoves(gameState);
  
  // Parse the move components
  const captureIndex = cleanNotation.indexOf('x');
  const hasCapture = captureIndex !== -1;
  
  // Get promotion if exists (e.g., e8=Q)
  const promotionMatch = cleanNotation.match(/=([QRBN])$/);
  const promotion = promotionMatch ? getPieceTypeFromLetter(promotionMatch[1]) : undefined;
  
  // Remove promotion from notation for parsing
  const withoutPromotion = promotion ? cleanNotation.replace(/=.$/, '') : cleanNotation;
  
  // Get destination square (last 2 characters)
  const destination = withoutPromotion.slice(-2);
  
  // Validate destination format
  if (!/^[a-h][1-8]$/.test(destination)) {
    return null;
  }
  
  const toSquare = algebraicToSquare(destination);
  
  // Determine piece type from first character (if uppercase)
  let pieceType = PieceType.Pawn;
  let startIndex = 0;
  
  const firstChar = withoutPromotion[0];
  if (/[KQRBN]/.test(firstChar)) {
    pieceType = getPieceTypeFromLetter(firstChar);
    startIndex = 1;
  }
  
  // Get disambiguation part (everything between piece letter and capture/destination)
  const beforeDest = withoutPromotion.slice(startIndex, -2);
  const disambiguationPart = hasCapture 
    ? beforeDest.replace('x', '') 
    : beforeDest;
  
  // Filter candidate moves
  let candidates = moves.filter(m => 
    m.piece.type === pieceType &&
    m.to === toSquare &&
    (promotion ? m.promotion === promotion : !m.promotion)
  );
  
  // Apply disambiguation if present
  if (disambiguationPart) {
    if (disambiguationPart.length === 1) {
      // Single character: could be file or rank
      if (/[a-h]/.test(disambiguationPart)) {
        // File disambiguation
        const file = disambiguationPart.charCodeAt(0) - 97; // a=0, b=1, ...
        candidates = candidates.filter(m => m.from % 8 === file);
      } else if (/[1-8]/.test(disambiguationPart)) {
        // Rank disambiguation
        const rank = parseInt(disambiguationPart) - 1;
        candidates = candidates.filter(m => Math.floor(m.from / 8) === rank);
      }
    } else if (disambiguationPart.length === 2) {
      // Full square disambiguation (e.g., "a1")
      if (/^[a-h][1-8]$/.test(disambiguationPart)) {
        const fromSquare = algebraicToSquare(disambiguationPart);
        candidates = candidates.filter(m => m.from === fromSquare);
      }
    }
  }
  
  // Should have exactly one candidate
  return candidates.length === 1 ? candidates[0] : null;
}

/**
 * Get piece type from letter
 */
function getPieceTypeFromLetter(letter: string): PieceType {
  switch (letter.toUpperCase()) {
    case 'K': return PieceType.King;
    case 'Q': return PieceType.Queen;
    case 'R': return PieceType.Rook;
    case 'B': return PieceType.Bishop;
    case 'N': return PieceType.Knight;
    default: return PieceType.Pawn;
  }
}

/**
 * Import game from PGN format
 */
export function importFromPGN(pgnText: string): PGNGame | null {
  try {
    // Parse tags
    const tags = parseTags(pgnText);
    
    // Get initial position (from FEN tag or standard)
    const initialFEN = tags.FEN || 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';
    let gameState = parseFEN(initialFEN);
    
    if (!gameState) {
      console.error('Invalid FEN in PGN:', initialFEN);
      return null;
    }
    
    // Extract moves section (everything after tags, before result)
    const moveSection = pgnText
      .replace(/\[.*?\]\n?/g, '') // Remove tags
      .replace(/\{[^}]*\}/g, '') // Remove comments
      .replace(/\([^)]*\)/g, '') // Remove variations
      .replace(/\$\d+/g, '') // Remove numeric annotations
      .trim();
    
    // Parse moves
    const moveTokens = moveSection
      .split(/\s+/) // Split on whitespace
      .filter(token => 
        token && 
        !token.match(/^\d+\.+$/) && // Remove move numbers (1., 2., etc.)
        !token.match(/^(1-0|0-1|1\/2-1\/2|\*)$/) // Remove results
      );
    
    const annotatedMoves: AnnotatedMove[] = [];
    
    for (const notation of moveTokens) {
      const move = parseAlgebraicMove(notation, gameState);
      
      if (!move) {
        console.error('Failed to parse move:', notation);
        console.error('Current position:', toFEN(gameState));
        return null;
      }
      
      // Make the move and get resulting state
      const newState = makeMove(gameState, move);
      
      // Store annotated move
      annotatedMoves.push({
        move,
        notation,
        fen: toFEN(newState),
      });
      
      gameState = newState;
    }
    
    return {
      tags,
      moves: annotatedMoves,
      initialFEN: tags.FEN,
    };
  } catch (error) {
    console.error('Error parsing PGN:', error);
    return null;
  }
}

/**
 * Load a PGN game into position history and move history
 * Returns the position history array and move history array
 */
export function loadPGN(pgnGame: PGNGame, initialState: GameState): {
  positionHistory: GameState[];
  moveHistory: AnnotatedMove[];
} {
  const positionHistory: GameState[] = [initialState];
  const moveHistory: AnnotatedMove[] = [];
  
  let currentState = initialState;
  
  for (const annotatedMove of pgnGame.moves) {
    // Make the move
    const newState = makeMove(currentState, annotatedMove.move);
    positionHistory.push(newState);
    
    // Use the notation from PGN (already imported)
    // Don't regenerate it to avoid circular dependency issues
    moveHistory.push({
      move: annotatedMove.move,
      notation: annotatedMove.notation,
      fen: toFEN(newState),
    });
    
    currentState = newState;
  }
  
  return { positionHistory, moveHistory };
}
